import { type IHeader, LanguageCode } from '@/interfaces';
import {
  type LocalizedField,
  type LocalizedItem,
  flattenItemList,
  headerDataModify,
  localizedString,
} from '@/utils/graphqlDataModify.utils';
import { getMasterTranslationLanguageCode } from '@/utils/language.utils';
import callContentful from './base.fetchers';

type RawHeaderItem = {
  mainText: LocalizedField;
  subText: LocalizedField;
  button: LocalizedField;
  itemList: LocalizedItem | null;
};

type GetHeader = (args: {
  isPreviewMode: boolean;
  languageCode: LanguageCode;
  slug: string;
}) => Promise<IHeader>;

const flattenHeaderItemList = (itemList: LocalizedItem | null, lang: string) => {
  if (!itemList?.itemList) return itemList;
  return {
    ...itemList,
    itemList: flattenItemList(itemList.itemList as Array<LocalizedItem>, lang),
  };
};

const getHeader: GetHeader = async ({ isPreviewMode, languageCode = LanguageCode.English, slug }) => {
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      headerCollection(
        preview: ${isPreviewMode},
        limit: 1,
      ) {
        items {
          itemList
          mainText {
            ${languageField}
          }
          subText {
            ${languageField}
          }
          button {
            ${languageField}
          }
        }
      }
    }
    `;

  const response = (await callContentful(query, isPreviewMode, slug, 'header.fetchers.getHeader')) as {
    data?: { headerCollection?: { items?: Array<RawHeaderItem | null> } };
  };
  const rawHeaderData: RawHeaderItem | null =
    response?.data?.headerCollection?.items?.filter((item): item is RawHeaderItem => item !== null)[0] ?? null;

  const transformedHeaderData = rawHeaderData
    ? {
        ...rawHeaderData,
        mainText: localizedString(rawHeaderData.mainText, languageField),
        subText: localizedString(rawHeaderData.subText, languageField),
        button: localizedString(rawHeaderData.button, languageField),
        itemList: flattenHeaderItemList(rawHeaderData.itemList, languageField),
      }
    : null;

  return headerDataModify(transformedHeaderData) as IHeader;
};

export { getHeader };
